/**
 * Renewal Health widget — sits on the Coach Accounting page.
 *
 * Pulls /api/coach/pack-auto-renew/health (snapshot covering the
 * `session_packs` auto-renewal pipeline) and renders:
 *   - 4 KPI tiles: Upcoming · Renewed 30d · Auto-Renew Revenue 30d · Failed 7d
 *   - Recent failures list (with the parent name + reason)
 *   - Recent successes list (with the parent + label)
 *   - Opted-out badge (parents who turned auto-renew OFF on an eligible pack)
 *
 * Coach + owner read this; no mutations live here. Failures link to the
 * client's lead-detail page via the user_id (the Communications + Billing
 * tab there is where the coach can re-charge or remove a pack).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, RefreshCw, AlertTriangle, CheckCircle2, Repeat, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtUSD = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

const fmtRelative = (iso) => {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        const mins = Math.round((Date.now() - d.getTime()) / 60000);
        if (mins < 1) return "just now";
        if (mins < 60) return `${mins}m ago`;
        const hrs = Math.round(mins / 60);
        if (hrs < 24) return `${hrs}h ago`;
        const days = Math.round(hrs / 24);
        if (days < 30) return `${days}d ago`;
        return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    } catch {
        return iso;
    }
};

function Kpi({ label, value, hint, tone = "default", testid }) {
    const toneClasses = {
        default: "border-zinc-800 bg-zinc-950",
        amber: "border-amber-900/60 bg-amber-950/30",
        red: "border-red-900/60 bg-red-950/30",
        green: "border-emerald-900/60 bg-emerald-950/20",
    };
    return (
        <div
            className={`border ${toneClasses[tone] || toneClasses.default} rounded-lg p-4`}
            data-testid={testid}
        >
            <div className="text-[10px] tracking-[0.18em] uppercase text-zinc-500 mb-2">
                {label}
            </div>
            <div className="text-2xl font-semibold text-white font-mono">{value}</div>
            {hint && <div className="text-[11px] text-zinc-500 mt-1">{hint}</div>}
        </div>
    );
}

export default function RenewalHealthWidget() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);

    const load = async (silent = false) => {
        if (!silent) setLoading(true);
        else setRefreshing(true);
        setError(null);
        try {
            const r = await axios.get(`${API}/coach/pack-auto-renew/health`, {
                withCredentials: true,
            });
            setData(r.data);
        } catch (e) {
            setError(e?.response?.data?.detail || "Failed to load renewal health");
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    if (loading) {
        return (
            <div
                className="border border-zinc-800 bg-zinc-950 rounded-lg p-8 flex items-center gap-3 text-zinc-400"
                data-testid="renewal-health-loading"
            >
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">Loading renewal health…</span>
            </div>
        );
    }

    if (error) {
        return (
            <div
                className="border border-red-900/60 bg-red-950/30 rounded-lg p-4 text-red-200 text-sm"
                data-testid="renewal-health-error"
            >
                {error}
            </div>
        );
    }

    if (!data) return null;

    const failedTone = data.failed_last_7d > 0 ? "red" : "default";

    return (
        <section data-testid="renewal-health-widget">
            <div className="flex items-end justify-between mb-3">
                <div>
                    <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                        <Repeat className="w-4 h-4 text-emerald-400" />
                        Pack Renewal Health
                    </h3>
                    <p className="text-[11px] text-zinc-500 mt-0.5">
                        Member · Auto packs scheduled to auto-renew when sessions hit zero.
                    </p>
                </div>
                <button
                    onClick={() => load(true)}
                    disabled={refreshing}
                    className="flex items-center gap-1.5 text-[11px] tracking-wider uppercase text-zinc-400 hover:text-white px-2 py-1 border border-zinc-800 rounded transition-colors disabled:opacity-40"
                    data-testid="renewal-health-refresh"
                >
                    <RefreshCw className={`w-3 h-3 ${refreshing ? "animate-spin" : ""}`} />
                    Refresh
                </button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <Kpi
                    label="Upcoming (≤2 left)"
                    value={data.upcoming_renewals}
                    hint="Will renew on next consume"
                    testid="kpi-upcoming"
                />
                <Kpi
                    label="Renewed · 30d"
                    value={data.renewed_last_30d}
                    hint={fmtUSD(data.renewed_revenue_cents_30d) + " revenue"}
                    tone="green"
                    testid="kpi-renewed"
                />
                <Kpi
                    label="Failed · 7d"
                    value={data.failed_last_7d}
                    hint={data.failed_last_7d > 0 ? "Needs follow-up" : "All clear"}
                    tone={failedTone}
                    testid="kpi-failed"
                />
                <Kpi
                    label="Opted Out"
                    value={data.opted_out_count}
                    hint="Active packs · auto-renew OFF"
                    tone="amber"
                    testid="kpi-opted-out"
                />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
                {/* Recent failures */}
                <div
                    className="border border-zinc-800 bg-zinc-950 rounded-lg overflow-hidden"
                    data-testid="recent-failures-card"
                >
                    <div className="px-4 py-2.5 border-b border-zinc-900 bg-zinc-900/50 flex items-center gap-2">
                        <AlertTriangle className="w-3.5 h-3.5 text-red-400" />
                        <span className="text-[10px] tracking-[0.18em] uppercase text-zinc-300 font-semibold">
                            Recent Failures
                        </span>
                    </div>
                    {data.recent_failures.length === 0 ? (
                        <div className="px-4 py-6 text-xs text-zinc-500 text-center">
                            No auto-renew failures recorded.
                        </div>
                    ) : (
                        <ul className="divide-y divide-zinc-900">
                            {data.recent_failures.map((f) => (
                                <li
                                    key={f.alert_id}
                                    className="px-4 py-2.5 text-xs"
                                    data-testid={`failure-row-${f.alert_id}`}
                                >
                                    <div className="flex items-center justify-between gap-2">
                                        <Link
                                            to={`/coach/leads/${f.user_id}`}
                                            className="text-white hover:text-red-300 transition-colors font-medium truncate"
                                        >
                                            {f.user_name || f.user_id}
                                        </Link>
                                        <span className="text-zinc-500 font-mono shrink-0">
                                            {fmtUSD(f.amount_cents)}
                                        </span>
                                    </div>
                                    <div className="text-zinc-500 truncate mt-0.5">
                                        {f.pack_name}
                                    </div>
                                    <div className="flex items-center justify-between mt-1 text-[10px]">
                                        <span className="text-red-300/80 truncate pr-2">
                                            {f.detail || "Card declined"}
                                        </span>
                                        <span className="text-zinc-600 shrink-0">
                                            {fmtRelative(f.created_at)}
                                        </span>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {/* Recent successes */}
                <div
                    className="border border-zinc-800 bg-zinc-950 rounded-lg overflow-hidden"
                    data-testid="recent-successes-card"
                >
                    <div className="px-4 py-2.5 border-b border-zinc-900 bg-zinc-900/50 flex items-center gap-2">
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                        <span className="text-[10px] tracking-[0.18em] uppercase text-zinc-300 font-semibold">
                            Recent Renewals
                        </span>
                    </div>
                    {data.recent_successes.length === 0 ? (
                        <div className="px-4 py-6 text-xs text-zinc-500 text-center">
                            No auto-renewals yet. They&apos;ll appear as soon as a Member · Auto pack
                            hits zero sessions.
                        </div>
                    ) : (
                        <ul className="divide-y divide-zinc-900">
                            {data.recent_successes.map((s) => (
                                <li
                                    key={s.pack_id}
                                    className="px-4 py-2.5 text-xs"
                                    data-testid={`success-row-${s.pack_id}`}
                                >
                                    <div className="flex items-center justify-between gap-2">
                                        <Link
                                            to={`/coach/leads/${s.user_id}`}
                                            className="text-white hover:text-emerald-300 transition-colors font-medium truncate flex items-center gap-1"
                                        >
                                            {s.user_name || s.user_id}
                                            <ChevronRight className="w-3 h-3 text-zinc-600" />
                                        </Link>
                                        <span className="text-emerald-400 font-mono shrink-0">
                                            {fmtUSD(s.total_cents)}
                                        </span>
                                    </div>
                                    <div className="text-zinc-500 truncate mt-0.5">
                                        {s.label}
                                    </div>
                                    <div className="text-[10px] text-zinc-600 mt-1 text-right">
                                        {fmtRelative(s.purchased_at)}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </section>
    );
}
